import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const paramCount = (tpl) => (((tpl?.components || []).find((c) => c.type === "BODY") || {}).text || "").match(/\{\{\d+\}\}/g)?.length || 0;
const hasImageHeader = (tpl) => (tpl?.components || []).some((c) => c.type === "HEADER" && c.format === "IMAGE");

export const OrderTemplateDialog = ({ conv, order, open, onClose }) => {
  const [templates, setTemplates] = useState(null);
  const [tplSel, setTplSel] = useState(null);
  const [params, setParams] = useState([]);
  const [headerUrl, setHeaderUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTplSel(null);
    setParams([]);
    setHeaderUrl("");
    setTemplates(null);
    api.get("/templates")
      .then((r) => setTemplates(r.data.filter((t) => t.status === "APPROVED")))
      .catch((e) => { setTemplates([]); toast.error(errText(e)); });
  }, [open]);

  if (!order) return null;

  const chips = [
    { label: "Customer", value: conv.contact_name || "" },
    { label: `Order #${order.number}`, value: `#${order.number}` },
    { label: order.status, value: order.status },
    { label: `Total ${order.currency === "INR" ? "₹" : order.currency}${order.total}`, value: `${order.currency === "INR" ? "₹" : order.currency}${order.total}` },
    { label: "Date", value: new Date(order.date).toLocaleDateString() },
  ];

  const fillChip = (value) => {
    setParams((arr) => {
      const idx = arr.findIndex((p) => !p);
      if (idx === -1) return arr;
      return arr.map((p, i) => (i === idx ? value : p));
    });
  };

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/conversations/${conv.id}/send-template`, {
        template_name: tplSel.name,
        language: tplSel.language,
        body_params: params,
        header_image_url: headerUrl || null,
      });
      toast.success(`Order update sent for #${order.number}`);
      onClose();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="card-tactile border-2 max-h-[85vh] overflow-y-auto" style={{ borderColor: "var(--cd-navy)" }} data-testid="order-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Send Order Update · #{order.number}</DialogTitle>
        </DialogHeader>
        <div className="text-xs font-semibold bg-[var(--cd-green)] border rounded-lg px-2 py-1.5" style={{ borderColor: "var(--cd-navy)" }}>
          {order.status} · {order.currency === "INR" ? "₹" : order.currency}{order.total} · to +{conv.phone}
        </div>
        {templates === null && <div className="font-hand text-lg text-amber-800">loading templates...</div>}
        {templates?.length === 0 && <div className="text-xs font-semibold text-red-700 bg-red-50 border-2 border-red-200 rounded-lg px-3 py-2">No approved templates available (check Meta token / Templates page).</div>}
        {templates?.length > 0 && (
          <>
            <label className="label-upper">Approved Template</label>
            <select
              data-testid="order-tpl-select"
              className="input-tactile"
              value={tplSel?.name || ""}
              onChange={(e) => {
                const t = templates.find((x) => x.name === e.target.value);
                setTplSel(t || null);
                setParams(t ? Array(paramCount(t)).fill("") : []);
                setHeaderUrl("");
              }}
            >
              <option value="">Select template...</option>
              {templates.map((t) => <option key={t.id || t.name} value={t.name}>{t.name} ({t.language})</option>)}
            </select>
            {tplSel && (
              <>
                <div className="text-xs bg-white border rounded-lg p-2 whitespace-pre-wrap" style={{ borderColor: "var(--cd-navy)" }}>
                  {((tplSel.components || []).find((c) => c.type === "BODY") || {}).text}
                </div>
                {params.length > 0 && (
                  <>
                    <div className="label-upper mb-0">Fill variables from order (tap a chip)</div>
                    <div className="flex flex-wrap gap-1.5">
                      {chips.map((c) => (
                        <button key={c.label} data-testid={`order-chip-${c.label.replace(/[^a-z0-9]/gi, "")}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full border-2 bg-[var(--cd-yellow)]" style={{ borderColor: "var(--cd-navy)" }} onClick={() => fillChip(c.value)}>
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {hasImageHeader(tplSel) && (
                  <>
                    <label className="label-upper">Header Image URL</label>
                    <input data-testid="order-tpl-header-input" className="input-tactile" placeholder="https://..." value={headerUrl} onChange={(e) => setHeaderUrl(e.target.value)} />
                  </>
                )}
                {params.map((p, i) => (
                  <div key={i}>
                    <label className="label-upper">Variable {"{{" + (i + 1) + "}}"}</label>
                    <input data-testid={`order-tpl-param-${i}`} className="input-tactile" value={p} onChange={(e) => setParams((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))} />
                  </div>
                ))}
                <button data-testid="order-tpl-send-btn" className="btn-navy justify-center" disabled={busy} onClick={send}>
                  <Send size={15} /> {busy ? "Sending..." : "Send Order Update"}
                </button>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
